import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLazyQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import ReactPaginate from 'react-paginate';

import { Spinner } from 'admin-components/components';
import { GET_SUBSCRIPTION_BOOKINGS } from 'store/queries';
import { BOOKINGS_SEARCH_TYPE } from 'constants/clubEurostar';
import Tabs from 'components/ui/Tabs';
import { BookingLink, Error, StyledTableCell, StyledTableHeader, StyledTableRow } from 'pages/ClubEurostarMember/ClubEurostarMember.styles';

import { FormContainer, TableContainer } from '../SubscriptionDetails.styles';
import moment from 'moment';
import { Column } from 'components/shared/Layout';
import { getBookingJourneysURL } from 'utils/urls';
import { hasNotCancelledThalysPassTickets } from '../Cancellation/Cancellations.utils';

const TABS = [
  {
    value: BOOKINGS_SEARCH_TYPE.UPCOMING_BOOKINGS,
    label: <FormattedMessage id="clubEurostar.upcoming" />
  },
  {
    value: BOOKINGS_SEARCH_TYPE.PAST_BOOKINGS,
    label: <FormattedMessage id="clubEurostar.travelled" />
  }
];

const ITEMS_PER_PAGE = 10;

const Bookings = ({ customerId, travelPassReference, membershipNumber, code }) => {
  const [selectedType, setSelectedType] = useState(BOOKINGS_SEARCH_TYPE.UPCOMING_BOOKINGS);
  const [subscriptionBookings, setSubscriptionBookings] = useState(undefined);
  const [subscriptionError, setSubscriptionError] = useState(undefined);
  const [page, setPage] = useState(0);

  const [getSubscriptionBookings, { loading }] = useLazyQuery(GET_SUBSCRIPTION_BOOKINGS, {
    fetchPolicy: 'network-only',
    onCompleted: ({ profileSubscriptionBookings }) => setSubscriptionBookings(profileSubscriptionBookings),
    onError: (error) => setSubscriptionError(error)
  });

  useEffect(() => {
    if (customerId) {
      if (travelPassReference) {
        getSubscriptionBookings({
          variables: {
            customerId,
            travelPassReference,
            bookingSearchType: selectedType,
            size: ITEMS_PER_PAGE,
            page: page + 1
          }
        });
      } else {
        setSubscriptionBookings(undefined);
        setSubscriptionError(undefined);
      }
    }
  }, [customerId, getSubscriptionBookings, travelPassReference, selectedType, page]);

  const { pagination = {}, items: bookingItems = [] } = subscriptionBookings || {};

  const pageCount = pagination?.totalPages ?? 0;
  const handlePageChange = (event) => setPage(event.selected);

  const renderReactPaginate = () => (
    <ReactPaginate
      data-cy="react-paginate"
      breakLabel="..."
      nextLabel=">"
      previousLabel="<"
      pageCount={pageCount}
      forcePage={page}
      pageRangeDisplayed={5}
      onPageChange={handlePageChange}
      renderOnZeroPageCount={null}
      breakClassName="page-item"
      breakLinkClassName="page-link"
      containerClassName="pagination"
      pageClassName="page-item"
      pageLinkClassName="page-link"
      previousClassName="page-item"
      previousLinkClassName="page-link"
      nextClassName="page-item"
      nextLinkClassName="page-link"
      activeClassName="active"
    />
  );

  const filteredItems = bookingItems.filter(({ tickets }) => !tickets.every(({ isCancelled }) => isCancelled));

  return (
    <>
      <h4>
        <FormattedMessage id="subscriptions.bookings" />
      </h4>
      <FormContainer data-cy="subscription-bookings">
        <TableContainer>
          <Tabs useHash={false} onClick={setSelectedType} selectedValue={selectedType} tabs={TABS} />
          {loading && (
            <Column alignItems="center" justifyContent="center">
              <Spinner />
            </Column>
          )}
          {!loading && (
            <>
              {subscriptionError && (
                <Error>
                  <FormattedMessage id="clubEurostar.defaultError" />
                </Error>
              )}
              {!subscriptionError && bookingItems && !bookingItems.length && (
                <Error>
                  <FormattedMessage
                    id={`clubEurostar.${
                      selectedType === BOOKINGS_SEARCH_TYPE.UPCOMING_BOOKINGS ? 'upcoming' : 'travelled'
                    }BookingsNotFound`}
                  />
                </Error>
              )}
              {!subscriptionError && bookingItems && bookingItems.length > 0 && (
                <>
                  {filteredItems.length > 0 ? (
                    filteredItems.map(({ reference, tickets }) => (
                      <div key={reference}>
                        <StyledTableRow columns={1}>
                          <Column>
                            <h3 data-cy="subscription-booking-reference">
                              <BookingLink
                                href={getBookingJourneysURL(reference, membershipNumber, travelPassReference, code)}
                                target="_blank"
                              >
                                {reference}
                              </BookingLink>
                            </h3>
                          </Column>
                        </StyledTableRow>
                        <StyledTableRow columns={9}>
                          <StyledTableHeader span={2} justifyContent="center">
                            <FormattedMessage id="searchBooking.departureDate" tagName="strong" />
                          </StyledTableHeader>
                          <StyledTableHeader span={2} justifyContent="center">
                            <FormattedMessage id="segmentInfo.depart" tagName="strong" />
                          </StyledTableHeader>
                          <StyledTableHeader span={2} justifyContent="center">
                            <FormattedMessage id="segmentInfo.arrive" tagName="strong" />
                          </StyledTableHeader>
                          <StyledTableHeader span={1} justifyContent="center">
                            <FormattedMessage id="journeySummary.class" tagName="strong" />
                          </StyledTableHeader>
                          <StyledTableHeader span={1} justifyContent="center">
                            <FormattedMessage id="clubEurostar.paxNo" tagName="strong" />
                          </StyledTableHeader>
                          <StyledTableHeader span={1} justifyContent="center">
                            <FormattedMessage id="journeySummaryBox.fareType" tagName="strong" />
                          </StyledTableHeader>
                        </StyledTableRow>
                        {tickets
                          .filter(({ isCancelled }) => !isCancelled)
                          .map((ticket) => (
                            <StyledTableRow
                              data-cy="subscription-booking-segment"
                              columns={9}
                              key={`${ticket.departureDate} ${ticket.departureTime}`}
                            >
                              <StyledTableCell span={2} justifyContent="center">
                                {moment(ticket.departureDate, 'YYYY-MM-DD').format('Do MMMM YYYY')}
                              </StyledTableCell>
                              <StyledTableCell span={2} justifyContent="center">
                                {ticket.origin}
                              </StyledTableCell>
                              <StyledTableCell span={2} justifyContent="center">
                                {ticket.destination}
                              </StyledTableCell>
                              <StyledTableCell span={1} justifyContent="center">
                                {ticket.serviceClass}
                              </StyledTableCell>
                              <StyledTableCell span={1} justifyContent="center">
                                {ticket.numberOfPassengers}
                              </StyledTableCell>
                              <StyledTableCell span={1} justifyContent="center">
                                {hasNotCancelledThalysPassTickets(ticket.products) ? 'Subscription' : 'Public'}
                              </StyledTableCell>
                            </StyledTableRow>
                          ))}
                      </div>
                    ))
                  ) : (
                    <Error>
                      <FormattedMessage
                        id={`clubEurostar.${
                          selectedType === BOOKINGS_SEARCH_TYPE.UPCOMING_BOOKINGS ? 'upcoming' : 'travelled'
                        }BookingsNotFound`}
                      />
                    </Error>
                  )}
                  {renderReactPaginate()}
                </>
              )}
            </>
          )}
        </TableContainer>
      </FormContainer>
    </>
  );
};

Bookings.propTypes = {
  customerId: PropTypes.string,
  travelPassReference: PropTypes.string,
  membershipNumber: PropTypes.string,
  code: PropTypes.string
};

export default Bookings;
